import { BaseDTO } from "@/models/BaseDto";
import { API_URL } from "./constant";

import { getAccessToken, getRefreshToken } from "./tokenManager";
import { JwtRefreshResponse } from "@/models/auth/Jwt";
import { useProfileStore } from "@/store/profile-store";

interface FetchOptions<TBody = unknown> {
  method?: string;
  body?: TBody;
  headers?: Record<string, string>;
  auth?: boolean;
  timeout?: number;
  request?: RequestInit;
}

const REFRESH_ENDPOINT = "auth/jwt/refresh";

const isFileOrBlob = (value: unknown): value is File | Blob => {
  if (typeof File !== "undefined" && value instanceof File) return true;
  if (typeof Blob !== "undefined" && value instanceof Blob) return true;
  return false;
};

const cloneFormData = (formData: FormData) => {
  const cloned = new FormData();
  formData.forEach((value, key) => {
    if (isFileOrBlob(value)) {
      const fileName =
        typeof File !== "undefined" && value instanceof File ? value.name : undefined;
      cloned.append(key, value, fileName);
    } else {
      cloned.append(key, value);
    }
  });
  return cloned;
};

const createRequestBody = (body: unknown): BodyInit | undefined => {
  if (body === undefined || body === null) {
    return undefined;
  }
  if (body instanceof FormData) {
    return cloneFormData(body);
  }
  if (typeof body === "string" || body instanceof Blob || body instanceof ArrayBuffer) {
    return body as BodyInit;
  }
  return JSON.stringify(body) as BodyInit;
};

// --- Token refresh (single-flight) -----------------------------------------
// Concurrent 401s must NOT each hit the refresh endpoint: with refresh-token
// rotation only the first would succeed and the rest would fail, leaving those
// requests stuck on the expired token. We share ONE in-flight refresh; every
// waiting caller resolves with the same new token and retries with it. The new
// token is written through the profile store, which persists it to localStorage
// (the source every request reads) and updates React consumers in one shot.
let refreshPromise: Promise<string | null> | null = null;

const requestRefresh = async (): Promise<string | null> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${API_URL}/${REFRESH_ENDPOINT}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      return null;
    }

    // The endpoint wraps its payload in a BaseDTO envelope ({ data: {...} }),
    // so the tokens live under `.data`. Reading them flat (the original bug)
    // always yielded `undefined`, so the refresh silently never applied and
    // every following request kept sending the expired token. Fall back to a
    // flat shape just in case the envelope is ever dropped.
    const json = await response.json();
    const payload: Partial<JwtRefreshResponse> = json?.data ?? json ?? {};
    if (!payload.access_token) {
      return null;
    }

    useProfileStore.getState().setTokens({
      access_token: payload.access_token,
      refresh_token: payload.refresh_token ?? getRefreshToken() ?? undefined,
    });
    return payload.access_token;
  } catch {
    return null;
  }
};

const refreshAccessToken = (): Promise<string | null> => {
  if (!refreshPromise) {
    refreshPromise = requestRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

export async function fetchInstance<TResponse = unknown, TBody = unknown>(
  endpoint: string,
  options: FetchOptions<TBody> = {},
): Promise<BaseDTO<TResponse>> {
  const {
    method = "GET",
    body,
    headers: additionalHeaders = {},
    auth = false,
    timeout = 20000,
    request: requestConfig = {},
  } = options;

  const url = `${API_URL}/${endpoint}`;
  const isFormDataBody = typeof FormData !== "undefined" && body instanceof FormData;

  const baseHeaders: HeadersInit = {
    ...additionalHeaders,
    ...(isFormDataBody
      ? {}
      : { "Content-Type": "application/json", accept: "application/json" }),
  };

  const executeRequest = async (tokenOverride?: string | null) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const mergedHeaders: HeadersInit = {
        ...requestConfig.headers,
        ...baseHeaders,
        ...(tokenOverride ? { Authorization: `Bearer ${tokenOverride}` } : {}),
      };

      const finalRequest: RequestInit = {
        ...requestConfig,
        method,
        headers: mergedHeaders,
        signal: controller.signal,
        body: createRequestBody(body),
      };

      return await fetch(url, finalRequest);
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const perform = async (): Promise<BaseDTO<TResponse>> => {
    try {
      // Always read the current token off localStorage so a refresh triggered
      // by another in-flight request is picked up here too.
      let response = await executeRequest(getAccessToken());

      if (response.status === 401 && auth) {
        const refreshedToken = await refreshAccessToken();
        if (refreshedToken) {
          response = await executeRequest(refreshedToken);
        }
      }

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = null;
        }
        return {
          errors: errorData?.errors ?? null,
          status: errorData?.status ?? response.status,
          message: errorData?.message ?? `HTTP ${response.status}: ${response.statusText}`,
          data: errorData?.data ?? null,
        } as BaseDTO<TResponse>;
      }

      return await response.json() as BaseDTO<TResponse>;

    } catch (e) {

      const message =
        e instanceof DOMException && e.name === "AbortError"
          ? "Request timed out"
          : e instanceof TypeError
            ? "Network error: Unable to connect to the server"
            : "An unexpected error occurred";

      throw new Error(message);

    }
  };


  return await perform();
}
